/*
 * pt-card-tracker.js — Pair-ie Tales product-card impression tracker
 * Plan: docs/product-card-tracking-plan.md  (Slice 1A — the storefront tracker)
 *
 * WHAT IT DOES
 *  - Watches every product card (.product-card-wrapper[data-product-id]) on
 *    collection / search / home listing pages.
 *  - Records when a card is >=50% visible continuously for 2s / 5s / 10s.
 *  - Records a PDP click on a card.
 *  - Mints a persistent first-party pt_visitor_id (+ rolling pt_session_id),
 *    reads Shopflo's shopflo_long_session_id cookie (bonus bridge key).
 *  - On add-to-cart intent, writes _pt_visitor_id / _pt_session_id as cart
 *    attributes so they land on the Shopify order (the browsing<->order stitch).
 *  - Captures entry UTMs/referrer once per session.
 *  - On product pages, records when the size chart is opened (size_chart_open).
 *  - On add-to-cart, records the product added (add_to_cart; source pdp/card).
 *  - Sends compact batched beacons (navigator.sendBeacon) on page-hide.
 *
 * PAGE-SPEED SAFE: ~3KB gzipped, no dependencies, loaded with `defer`,
 * IntersectionObserver fires only on threshold crossings (no scroll handler),
 * one beacon per page on exit (off main thread), adds no DOM/images => no CLS.
 *
 * DEBUG MODE (no backend needed): in the browser console run
 *     localStorage.setItem('pt_debug','1')
 * then reload a collection page and scroll — you'll see [pt-card] logs and a
 * DRY-RUN of exactly what would be sent. Turn off with
 *     localStorage.removeItem('pt_debug')
 */
(function () {
  'use strict';

  // ----- config -----
  var CONFIG = {
    endpoint: 'https://pt-card-collector-cbzg4iqliq-el.a.run.app/collect', // Cloud Run collector; set '' to force DRY-RUN (logs only)
    tiers: [2000, 5000, 10000],
    visibility: 0.5,         // fraction of the card that must be in view
    sessionTtlMin: 30        // rolling session window
  };

  var DEBUG = false;
  try { DEBUG = (localStorage.getItem('pt_debug') === '1') || window.PT_DEBUG === true; } catch (e) {}
  function log() { if (DEBUG && window.console) console.log.apply(console, ['[pt-card]'].concat([].slice.call(arguments))); }

  // ----- small helpers -----
  function uuid() {
    try { return crypto.randomUUID(); } catch (e) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8); return v.toString(16);
    });
  }
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function getCookie(name) {
    try {
      var parts = ('; ' + document.cookie).split('; ' + name + '=');
      if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    } catch (e) {}
    return null;
  }
  // Match a cookie by a regex on its NAME — GA4/CL cookies carry an
  // environment-specific container/workspace suffix, so we scan by prefix.
  function cookieByName(re) {
    try {
      var all = document.cookie.split('; ');
      for (var i = 0; i < all.length; i++) {
        var eq = all[i].indexOf('=');
        if (eq > 0 && re.test(all[i].slice(0, eq))) return decodeURIComponent(all[i].slice(eq + 1));
      }
    } catch (e) {}
    return null;
  }
  // Cross-system identity ids already present in the browser (raw strings;
  // parsed downstream in the daily batch merge so parsing fixes need no redeploy).
  function identityCookies() {
    return {
      ga_cookie:         getCookie('_ga'),                       // GA4 client id  GA1.1.<cid>
      ga_session_cookie: cookieByName(/^_ga_/),                  // GA4 session    GS2.1.s<sid>...
      shopify_y:         getCookie('_shopify_y'),                // Shopify persistent visitor
      cl_uid:            cookieByName(/^cl[^=]*_uid$/),          // CustomerLabs user id
      cl_session_cookie: cookieByName(/^cl[^=]*_userSession$/),  // CL session {"sid":...}
      fbp:               getCookie('_fbp')                       // Meta browser id
    };
  }
  function isMobile() { return /Mobi|Android/i.test(navigator.userAgent); }
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  // ----- identity -----
  function getVisitorId() {
    var v = lsGet('pt_visitor_id');
    if (!v) { v = uuid(); lsSet('pt_visitor_id', v); }
    return v;
  }
  function getSessionId() {
    var now = Date.now();
    var sid = lsGet('pt_session_id');
    var exp = parseInt(lsGet('pt_session_exp') || '0', 10);
    if (!sid || now > exp) { sid = uuid(); lsSet('pt_session_id', sid); lsSet('pt_session_start', String(now)); }
    lsSet('pt_session_exp', String(now + CONFIG.sessionTtlMin * 60000));
    return sid;
  }

  var VISITOR = getVisitorId();
  var SESSION = getSessionId();
  // ATC offer popup: on a later page in the same session, load pt-offer.js again if this session already had an add-to-cart
  try { if (lsGet('pt_atc_sess') === SESSION && window.PT_ATC && window.PT_ATC.src && !window.__ptOffer) { var o0 = document.createElement('script'); o0.src = window.PT_ATC.src; o0.async = true; document.head.appendChild(o0); } } catch (e) {}
  var SHOPFLO_LS = getCookie('shopflo_long_session_id');
  var PAGE_VIEW_ID = uuid();
  // Promo-email click: links carry ?pcid=<send id>&pl=<link name>. Log the landing in the background (same-origin, no redirect hop); once per link per session.
  try {
    var ecq = new URLSearchParams(location.search), ecid = ecq.get('pcid');
    if (ecid && /^[A-Za-z0-9_-]{6,32}$/.test(ecid)) {
      var eck = 'pt_ec_' + ecid + '_' + (ecq.get('pl') || '');
      if (!sessionStorage.getItem(eck)) {
        sessionStorage.setItem(eck, '1');
        fetch('/apps/atc/ck?c=' + ecid + '&l=' + encodeURIComponent(ecq.get('pl') || '') + '&u=' + encodeURIComponent(location.pathname), { keepalive: true, credentials: 'omit' });
      }
    }
  } catch (e) {}
  var COLLECTION = (location.pathname.match(/^\/collections\/([^\/?#]+)/) || [])[1] || null;

  function pageType() {
    var p = location.pathname;
    if (/^\/collections(\/|$)/.test(p)) return 'collection';
    if (/^\/search/.test(p)) return 'search';
    if (p === '/') return 'home';
    return 'other';
  }
  var PAGE_TYPE = pageType();

  // ----- transport -----
  function send(obj) {
    var body = JSON.stringify(obj);
    if (!CONFIG.endpoint) { log('DRY-RUN send →', obj); return; }
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(CONFIG.endpoint, new Blob([body], { type: 'application/json' }));
      } else {
        fetch(CONFIG.endpoint, { method: 'POST', body: body, headers: { 'Content-Type': 'application/json' }, keepalive: true });
      }
    } catch (e) { log('send error', e); }
  }

  // ----- session entry attribution (once per session) -----
  function captureSession() {
    if (lsGet('pt_session_logged') === SESSION) return;
    var qs;
    try { qs = new URLSearchParams(location.search); } catch (e) { qs = { get: function () { return null; } }; }
    var idc = identityCookies();
    send({
      type: 'session',
      visitor_id: VISITOR, session_id: SESSION, shopflo_long_session_id: SHOPFLO_LS,
      ts: new Date().toISOString(),
      landing_path: location.pathname,
      referrer: document.referrer || null,
      utm_source: qs.get('utm_source'), utm_medium: qs.get('utm_medium'),
      utm_campaign: qs.get('utm_campaign'), utm_term: qs.get('utm_term'), utm_content: qs.get('utm_content'),
      fbclid: qs.get('fbclid'), gclid: qs.get('gclid'),
      device: isMobile() ? 'mobile' : 'desktop',
      // cross-system identity ids (raw cookie strings; parsed in the batch merge)
      ga_cookie: idc.ga_cookie, ga_session_cookie: idc.ga_session_cookie,
      shopify_y: idc.shopify_y, cl_uid: idc.cl_uid,
      cl_session_cookie: idc.cl_session_cookie, fbp: idc.fbp
    });
    lsSet('pt_session_logged', SESSION);
  }

  // ----- impression buffer (one row per product per page-view) -----
  var buffer = {}; // product_id -> row
  function ensureRow(pid, position) {
    if (!buffer[pid]) {
      buffer[pid] = { product_id: pid, position: position ? parseInt(position, 10) : null,
        reached_2s: false, reached_5s: false, reached_10s: false, max_tier: 0, clicked: false };
    }
    return buffer[pid];
  }

  // ----- IntersectionObserver -----
  var observer, globalPos = 1, cardState = new WeakMap();
  function onIntersect(entries) {
    each(entries, function (entry) {
      var el = entry.target;
      var pid = el.getAttribute('data-product-id');
      if (!pid) return;
      var st = cardState.get(el) || {}; cardState.set(el, st);
      if (entry.isIntersecting && entry.intersectionRatio >= CONFIG.visibility) {
        if (st.timers) return; // already counting
        st.timers = [];
        var position = el.getAttribute('data-pt-pos');
        CONFIG.tiers.forEach(function (ms) {
          st.timers.push(setTimeout(function () {
            var sec = ms / 1000;
            var row = ensureRow(pid, position);
            row['reached_' + sec + 's'] = true;
            if (sec > row.max_tier) row.max_tier = sec;
            log('seen', pid, sec + 's');
            if (sec === 10) flush('tier10'); // safety flush on the strongest signal
          }, ms));
        });
      } else if (st.timers) {
        st.timers.forEach(clearTimeout);
        st.timers = null;
      }
    });
  }

  function attachCards(root) {
    var cards = (root || document).querySelectorAll('.product-card-wrapper[data-product-id]');
    each(cards, function (el) {
      if (el.__ptObserved) return;
      el.__ptObserved = true;
      if (!el.getAttribute('data-pt-pos')) el.setAttribute('data-pt-pos', String(globalPos++));
      observer.observe(el);
    });
  }

  // ----- PDP click capture -----
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var link = e.target.closest('a[href*="/products/"]');
    if (!link) return;
    var card = e.target.closest('.product-card-wrapper[data-product-id]');
    if (!card) return;
    var pid = card.getAttribute('data-product-id');
    ensureRow(pid, card.getAttribute('data-pt-pos')).clicked = true;
    log('click → PDP', pid);
    flush('click');
  }, true);

  // ----- add-to-cart intent → write cart attributes (the order stitch) -----
  function setCartAttributes() {
    if (window.__ptCartSet) return;
    window.__ptCartSet = true;
    try {
      fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ attributes: { _pt_visitor_id: VISITOR, _pt_session_id: SESSION } })
      }).then(function () { log('cart attributes set', VISITOR); })
        .catch(function () { window.__ptCartSet = false; });
    } catch (e) { window.__ptCartSet = false; }
  }
  // Resolve the product being added — from the enclosing card (collection/search)
  // or the current PDP — and beacon ONE add_to_cart per product per page-view.
  var atcSent = {};
  function sendAddToCart(pid, handle, source) {
    if (!pid || atcSent[pid]) return;
    atcSent[pid] = true;
    send({
      type: 'add_to_cart',
      visitor_id: VISITOR, session_id: SESSION, shopflo_long_session_id: SHOPFLO_LS,
      page_view_id: PAGE_VIEW_ID, page_type: PAGE_TYPE, collection: COLLECTION,
      device: isMobile() ? 'mobile' : 'desktop',
      product_id: String(pid), product_handle: handle || null, source: source,
      bms_arm: (window.__ptBms && window.__ptBms.arm) || null,
      bms_pers: (window.__ptBms && window.__ptBms.pers) || null,
      ts: new Date().toISOString()
    });
    try { document.dispatchEvent(new CustomEvent('pt:atc', { detail: { id: String(pid), source: source } })); } catch (e) {}
    log('add_to_cart', pid, source);
    // ATC offer popup: note the first add-to-cart of this session, then load pt-offer.js (it checks the on/off flag itself)
    try {
      if (lsGet('pt_atc_sess') !== SESSION) { lsSet('pt_atc_sess', SESSION); lsSet('pt_atc_ts', String(Date.now())); lsSet('pt_atc_src', String(source || '')); }
      if (window.PT_ATC && window.PT_ATC.src && !window.__ptOffer) { var o = document.createElement('script'); o.src = window.PT_ATC.src; o.async = true; document.head.appendChild(o); }
    } catch (e) {}
  }
  function resolveAtcProduct(target) {
    var card = target && target.closest && target.closest('.product-card-wrapper[data-product-id]');
    if (card) return { id: card.getAttribute('data-product-id'), handle: null, source: 'card' };
    var qm = target && target.closest && target.closest('[id^="qvm-"]'), qid = qm && /-(\d+)$/.exec(qm.id);
    if (qid) return { id: qid[1], handle: null, source: 'card' };
    var p = currentPdpProduct();
    if (p.id) return { id: p.id, handle: p.handle, source: 'pdp' };
    return null;
  }
  // Lets code outside this file (the campaign video player) report an add-to-cart through the same path.
  window.ptTrackAtc = function (pid, handle, source) { setCartAttributes(); sendAddToCart(pid, handle, source); };
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var atcEl = e.target.closest('button[name="add"], .card-atc-plus, .quick-add__submit, [data-pt-atc]');
    if (atcEl) {
      setCartAttributes();
      var pr = resolveAtcProduct(atcEl);
      if (pr) sendAddToCart(pr.id, pr.handle, pr.source);
    }
  }, true);
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (f && f.action && /\/cart\/add/.test(f.action)) {
      setCartAttributes();
      var p = currentPdpProduct();
      if (p.id) sendAddToCart(p.id, p.handle, 'pdp');
    }
  }, true);

  // ----- size-chart open capture (PDP) -----
  // showPopup() in assets/size-guide.js adds class "active" to .overlay.size-guide.
  // We watch for that and beacon ONE size_chart_open per product per page-view.
  var sizeChartSent = {};
  function currentPdpProduct() {
    var id = null;
    try {
      if (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product)
        id = ShopifyAnalytics.meta.product.id;
    } catch (e) {}
    var m = location.pathname.match(/\/products\/([^\/?#]+)/);
    return { id: id != null ? String(id) : null, handle: m ? m[1] : null };
  }
  function sendSizeChartOpen() {
    var prod = currentPdpProduct();
    if (!prod.id || sizeChartSent[prod.id]) return;   // opened-once per product per page-view
    sizeChartSent[prod.id] = true;
    send({
      type: 'size_chart_open',
      visitor_id: VISITOR, session_id: SESSION, shopflo_long_session_id: SHOPFLO_LS,
      page_view_id: PAGE_VIEW_ID, page_type: 'product', device: isMobile() ? 'mobile' : 'desktop',
      product_id: prod.id, product_handle: prod.handle, ts: new Date().toISOString()
    });
    log('size_chart_open', prod.id, prod.handle);
  }
  function setupSizeChartTracking() {
    var overlay = document.querySelector('.overlay.size-guide');
    if (!overlay || overlay.__ptSizeObserved) return;
    overlay.__ptSizeObserved = true;
    try {
      new MutationObserver(function () {
        if (overlay.classList.contains('active')) sendSizeChartOpen();
      }).observe(overlay, { attributes: true, attributeFilter: ['class'] });
    } catch (e) {}
  }

  // ----- PDP gallery engagement (one beacon per page-view, sent on page-hide) -----
  // Records only: furthest image reached, and seconds each image was >=60% on screen while the tab was visible.
  // IntersectionObserver on the gallery slides (no scroll handlers). Position = DOM order = catalogue image number.
  // Sent as a single cumulative 'pdp_gallery' beacon (latest per page_view_id wins downstream).
  var gal = { seen: {}, dwell: {}, max: 0, n: 0, t0: {}, sent: '' };
  function galTick(now) {
    for (var k in gal.t0) if (gal.t0.hasOwnProperty(k) && gal.t0[k]) {
      gal.dwell[k] = (gal.dwell[k] || 0) + (now - gal.t0[k]) / 1000;
      gal.t0[k] = now;
    }
  }
  function setupGalleryTracking() {
    if (!/\/products\//.test(location.pathname) || !('IntersectionObserver' in window)) return;
    var slides = document.querySelectorAll('.product__media-list .product__media-item');
    if (!slides.length) return;
    gal.n = slides.length;
    var io = new IntersectionObserver(function (entries) {
      var now = Date.now();
      galTick(now);
      entries.forEach(function (en) {
        var pos = en.target.__ptPos;
        if (en.isIntersecting && document.visibilityState === 'visible') {
          gal.t0[pos] = now;
          if (pos > gal.max) gal.max = pos;
        } else {
          gal.t0[pos] = 0;
        }
      });
    }, { threshold: 0.6 });
    each(slides, function (s, i) { s.__ptPos = i + 1; io.observe(s); });
  }
  function flushGallery(hidden) {
    if (!gal.n) return;
    var now = Date.now();
    galTick(now);
    if (hidden) for (var k in gal.t0) gal.t0[k] = 0;
    else if (document.visibilityState === 'visible') for (var j in gal.t0) if (gal.t0[j]) gal.t0[j] = now;
    var d = {};
    for (var p in gal.dwell) if (gal.dwell.hasOwnProperty(p) && gal.dwell[p] >= 1) d[p] = Math.round(gal.dwell[p]);
    var sig = gal.max + '|' + JSON.stringify(d);
    if (!gal.max || sig === gal.sent) return;
    gal.sent = sig;
    var prod = currentPdpProduct();
    if (!prod.id) return;
    var bms = window.__ptBms || {};
    send({
      type: 'pdp_gallery',
      visitor_id: VISITOR, session_id: SESSION, page_view_id: PAGE_VIEW_ID,
      device: isMobile() ? 'mobile' : 'desktop', product_id: prod.id, product_handle: prod.handle,
      n_images: gal.n, max_image: gal.max, dwell_s: d,
      bms_arm: bms.arm || null, bms_pers: bms.pers || null, bms_pct: bms.pct == null ? null : bms.pct,
      ts: new Date().toISOString()
    });
    log('pdp_gallery', gal.max, d);
  }

  // ----- flush -----
  function flush(reason) {
    var rows = [];
    for (var pid in buffer) if (buffer.hasOwnProperty(pid)) {
      var r = buffer[pid];
      if (r.max_tier > 0 || r.clicked) rows.push(r);
    }
    if (!rows.length) return;
    send({
      type: 'impressions', reason: reason || 'flush',
      visitor_id: VISITOR, session_id: SESSION, shopflo_long_session_id: SHOPFLO_LS,
      page_view_id: PAGE_VIEW_ID, page_type: PAGE_TYPE, collection: COLLECTION,
      ts: new Date().toISOString(), device: isMobile() ? 'mobile' : 'desktop',
      items: rows
    });
    log('flush[' + (reason || 'flush') + ']', rows.length, 'rows', rows);
  }
  // server de-dupes on (page_view_id, product_id) via MERGE, so re-sending the
  // full buffer on each flush is safe and keeps the latest state.
  ['visibilitychange', 'pagehide'].forEach(function (ev) {
    document.addEventListener(ev, function () {
      if (ev === 'visibilitychange' && document.visibilityState !== 'hidden') return;
      flushGallery(true);
      flush('pagehide');
    });
  });

  // ----- init -----
  function init() {
    setupGalleryTracking();
    setupSizeChartTracking();   // PDP size-chart open capture (independent of IntersectionObserver)
    if (!('IntersectionObserver' in window)) { log('IntersectionObserver unsupported — skipping'); return; }
    observer = new IntersectionObserver(onIntersect, { threshold: [CONFIG.visibility] });
    attachCards(document);
    try {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) { attachCards(document); break; }
        }
      }).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    captureSession();
    log('init', { visitor: VISITOR, session: SESSION, page_type: PAGE_TYPE, collection: COLLECTION, shopflo_long_session_id: SHOPFLO_LS, endpoint: CONFIG.endpoint || '(dry-run)' });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();